/**
 * useSupportChat — Hook para o webchat de suporte com o agente Lucas
 *
 * Gerencia estado de conversa, envio de mensagens, upload de screenshots,
 * e eventos de socket em tempo real.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPost, apiUpload } from "@/lib/api";
import { useSocket } from "@/contexts/SocketContext";

const SUPPORT_API = "/api/internal/support";

export function useSupportChat() {
  const queryClient = useQueryClient();
  const { on, socket } = useSocket();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  // O que o Lucas esta fazendo agora ("consultando a Central de Ajuda").
  const [activity, setActivity] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  // Quem está do outro lado. Vem do servidor — é derivado do ticket aberto no
  // nosso Inbox, então o front nunca precisa adivinhar nem "desligar" nada.
  const [handover, setHandover] = useState(null);
  const subscribedRef = useRef(false);
  /**
   * O estado do atendimento, legível de dentro dos ouvintes de socket.
   *
   * Eles são registrados uma vez (dependem só de `on`), então lê-lo direto do
   * estado devolveria sempre o valor do primeiro render — e o rótulo de
   * "digitando" nunca saberia que há gente do outro lado.
   */
  const handoverRef = useRef(null);
  useEffect(() => {
    handoverRef.current = handover;
  }, [handover]);

  // ── Load active conversation ────────────────────────────────────────
  const conversationQuery = useQuery({
    queryKey: ["support-conversation"],
    queryFn: async () => {
      const res = await apiGet(`${SUPPORT_API}/conversation`);
      if (!res.ok) throw new Error("Erro ao carregar conversa");
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Sync messages when conversation loads
  useEffect(() => {
    if (conversationQuery.data) {
      setConversationId(conversationQuery.data.conversationId);
      setHandover(conversationQuery.data.handover || null);
      if (conversationQuery.data.messages?.length) {
        // `messageId` é o id PERSISTIDO, e é ele que a avaliação usa como
        // chave. O `id` local existe só para a lista do React e, nas mensagens
        // recém-chegadas, nem sempre é o do banco.
        setMessages(
          conversationQuery.data.messages.map((m) =>
            m.role === "assistant" ? { ...m, messageId: m.id } : m,
          ),
        );
      }
    }
  }, [conversationQuery.data]);

  // ── Socket subscription ─────────────────────────────────────────────
  useEffect(() => {
    if (!socket || subscribedRef.current) return;

    socket.emit("support:subscribe");
    subscribedRef.current = true;

    return () => {
      socket.emit("support:unsubscribe");
      subscribedRef.current = false;
    };
  }, [socket]);

  // Listen for real-time messages
  useEffect(() => {
    const unsubMsg = on("support:message", (data) => {
      // `agent` é uma PESSOA do nosso time respondendo pelo Inbox. Chega pelo
      // mesmo evento porque, para quem lê, é a mesma conversa — só muda quem
      // escreveu, e a tela precisa mostrar isso.
      // `system` é aviso de tela — "atendimento encerrado". Entra na conversa
      // como divisor, não como fala de ninguém.
      if (data.role === "assistant" || data.role === "agent" || data.role === "system") {
        setMessages((prev) => [
          ...prev,
          {
            id: `${data.role}-${Date.now()}`,
            role: data.role,
            content: data.content,
            blocks: data.blocks || [],
            handoff: data.handoff || null,
            // O print que o agente manda é metade da resposta.
            attachments: data.attachments || [],
            // A citação é a outra metade: sem ela, "isso aqui" chega solto.
            replyTo: data.replyTo || null,
            /**
             * `messageId` chega com significados diferentes conforme quem
             * falou, e por isso é separado aqui em vez de guardado nos dois
             * campos: no turno do Lucas é a mensagem da CONVERSA, chave da
             * avaliação; no turno do agente é a mensagem do INBOX, chave da
             * citação. Trocar um pelo outro avaliaria a resposta errada — ou
             * citaria uma mensagem que não existe do outro lado.
             */
            ...(data.role === "agent"
              ? { inboxMessageId: data.messageId || null }
              : { messageId: data.messageId || null }),
            createdAt: new Date().toISOString(),
          },
        ]);
        if (data.conversationId) setConversationId(data.conversationId);
      }
    });

    // O estado do atendimento humano muda por fora da conversa: alguém assume
    // ou resolve o ticket no Inbox, e a tela tem que acompanhar sem recarregar.
    const unsubHandover = on("support:handover", (data) => {
      setHandover(data?.active ? data : null);
    });

    /**
     * A reação do time, chegando à mensagem certa.
     *
     * Vem por `inboxMessageId` porque a reação vive na mensagem do Inbox — a
     * conversa daqui só guarda a referência.
     */
    const unsubReaction = on("support:reaction", (data) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.inboxMessageId === data.messageId
            ? {
                ...m,
                reactions: data.emoji
                  ? [{ emoji: data.emoji, fromAgent: true, fromContact: false }]
                  : [],
              }
            : m,
        ),
      );
    });

    const unsubTyping = on("support:typing", (data) => {
      setIsTyping(data.isTyping);
      // Fim do turno limpa o rotulo: sem isto ele congela na ultima ferramenta.
      if (!data.isTyping) setActivity(null);
      // Quando quem digita é uma PESSOA, os pontinhos ganham rótulo — sem nome:
      // o atendente escrevendo precisa ser distinguível do robô pensando, e
      // isso se resolve dizendo que é o time, não quem é.
      else if (handoverRef.current) setActivity("Nosso time está digitando…");
    });

    const unsubActivity = on("support:activity", (data) => {
      setActivity(data?.label || null);
    });

    return () => {
      unsubMsg();
      unsubHandover();
      unsubReaction();
      unsubTyping();
      unsubActivity();
    };
  }, [on]);

  // ── Send message mutation ───────────────────────────────────────────
  const sendMutation = useMutation({
    // `attachments` serve a dois propósitos: o balão local mostra miniatura e
    // pill, e o servidor roteia cada arquivo pelo tipo — imagem vai para visão,
    // `.twflow.json` é lido como estrutura, o resto é declarado não-lido.
    // `imageUrl` continua indo por compatibilidade com o caminho antigo.
    mutationFn: async ({ message, imageUrl, attachments, replyTo }) => {
      const res = await apiPost(`${SUPPORT_API}/chat`, {
        message,
        imageUrl,
        attachments: attachments?.map((a) => ({
          url: a.url,
          name: a.name,
          mimeType: a.mimeType,
          // O cartão de download exibe o tamanho; sem ele o arquivo volta do
          // servidor com nome e mais nada.
          size: a.size,
        })),
        // O id citado é o da mensagem no INBOX: é a chave que a citação de lá
        // exige, e o id local desta conversa não existe naquele lado.
        replyTo: replyTo || null,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Erro ao enviar mensagem");
      }
      return res.json();
    },
    onMutate: ({ message, imageUrl, attachments, replyTo }) => {
      // Optimistic: add user message immediately. É esta inserção que faz a
      // transição para o chat abrir já com a pergunta dentro e o Lucas
      // "pensando" — a animação corre em cima de conteúdo vivo.
      const content = message || "";
      if (!content && !imageUrl && !attachments?.length) return;
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          role: "user",
          content,
          imageUrl: imageUrl || null,
          attachments: attachments || [],
          replyTo: replyTo || null,
          createdAt: new Date().toISOString(),
        },
      ]);
    },
    /**
     * O 202 diz só que o pedido foi aceito.
     *
     * A resposta, o handover e o id da conversa chegam por socket
     * (`support:message`, `support:handover`) — o turno pode levar minutos, e
     * requisição HTTP não é lugar para tarefa longa. O socket já era o caminho
     * principal antes disto: o corpo do POST era, no comentário original,
     * "fallback com dedupe". Como fallback ele agora traria um balão vazio.
     */
    onSuccess: () => {},
    onError: (err) => {
      toast.error(err.message || "Erro ao enviar mensagem");
      // Remove optimistic user message on error
      setMessages((prev) => prev.slice(0, -1));
    },
  });

  // ── Upload screenshot ───────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiUpload(`${SUPPORT_API}/upload`, formData);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Erro no upload");
      }
      return res.json();
    },
    onError: (err) => {
      toast.error(err.message || "Erro ao fazer upload da imagem");
    },
  });

  /**
   * "Nova conversa" limpa a tela na hora, não quando o servidor termina.
   *
   * Fechar gera o resumo da conversa com o modelo ANTES de responder, e são
   * cinco segundos ou mais olhando a conversa antiga com o botão já clicado.
   * Quem pediu conversa nova quer escrever, não esperar por um resumo que vai
   * ler depois — se ler.
   *
   * O resumo continua sendo gerado no mesmo pedido: só o histórico depende dele,
   * e o histórico só é recarregado quando ele fica pronto. Se o fechamento
   * falhar, a conversa volta como estava.
   */
  const closeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`${SUPPORT_API}/close`, {});
      if (!res.ok) throw new Error("Erro ao fechar conversa");
      return res.json();
    },
    onMutate: () => {
      const previous = { messages, conversationId };
      setMessages([]);
      setConversationId(null);
      // Conversa nova também para o turno em curso — o servidor aborta no
      // `/close`; aqui a espera some da tela na hora.
      setIsTyping(false);
      setActivity(null);
      return previous;
    },
    onError: (_err, _vars, previous) => {
      // Limpar a tela e não fechar de verdade perderia a conversa de vista.
      if (previous) {
        setMessages(previous.messages);
        setConversationId(previous.conversationId);
      }
      toast.error("Não consegui finalizar a conversa.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-conversation"] });
      queryClient.invalidateQueries({ queryKey: ["support-history"] });
      toast.success("Conversa finalizada");
    },
  });

  /**
   * Parar a resposta que está sendo gerada.
   *
   * A espera some na hora (`onMutate`), sem esperar o servidor confirmar: quem
   * clicou em parar já decidiu. O turno abortado não manda mais balão nenhum, e
   * um turno que tenha acabado no mesmo instante é inofensivo — parar o que já
   * terminou não faz mal.
   */
  const stopMutation = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`${SUPPORT_API}/stop`, {});
      if (!res.ok) throw new Error("Erro ao interromper");
      return res.json();
    },
    onMutate: () => {
      setIsTyping(false);
      setActivity(null);
    },
    // Falhar aqui não volta atrás: no pior caso a resposta chega mesmo assim.
    onError: () => {},
  });

  /**
   * A avaliação de uma resposta.
   *
   * O voto pinta na hora e só volta atrás se o servidor recusar: a pessoa
   * clicou num polegar, não pediu para esperar. O comentário chega num segundo
   * envio do MESMO voto — a chave é a mensagem, então o servidor sobrescreve em
   * vez de criar outro registro.
   */
  const rateMutation = useMutation({
    mutationFn: async ({ messageId, helpful, comment }) => {
      const res = await apiPost(`${SUPPORT_API}/messages/${messageId}/feedback`, {
        helpful,
        comment,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Erro ao registrar a avaliação");
      }
      return res.json();
    },
    onMutate: ({ messageId, helpful, comment }) => {
      const previous = messages.find((m) => m.messageId === messageId)?.feedback || null;
      setMessages((prev) =>
        prev.map((m) =>
          m.messageId === messageId
            ? { ...m, feedback: { helpful, comment: comment || null } }
            : m,
        ),
      );
      return { messageId, previous };
    },
    onError: (err, _vars, context) => {
      if (context) {
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === context.messageId ? { ...m, feedback: context.previous } : m,
          ),
        );
      }
      toast.error(err.message || "Erro ao registrar a avaliação");
    },
  });

  /**
   * Chamar uma pessoa — sem sair da conversa.
   *
   * Abre um atendimento no Inbox da TouchWorks. A partir daí quem responde é
   * gente, e a tela passa a dizer isso. Não há canal externo nem promessa de
   * retorno por outro caminho: a conversa continua exatamente onde está.
   */
  const handoverMutation = useMutation({
    mutationFn: async (reason) => {
      const res = await apiPost(`${SUPPORT_API}/handover`, { reason });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Não consegui chamar uma pessoa agora.");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setHandover({ active: true, ticketId: data.ticketId, ticketNumber: data.ticketNumber });
      toast.success(
        data.created
          ? "Pronto, chamei nosso time. Pode continuar escrevendo por aqui."
          : "Seu atendimento já está aberto. Pode continuar escrevendo por aqui.",
      );
    },
    onError: (err) => {
      toast.error(err.message || "Não consegui chamar uma pessoa agora.");
    },
  });

  // ── History ─────────────────────────────────────────────────────────
  const historyQuery = useQuery({
    queryKey: ["support-history"],
    queryFn: async () => {
      const res = await apiGet(`${SUPPORT_API}/history?limit=10`);
      if (!res.ok) throw new Error("Erro ao buscar histórico");
      return res.json();
    },
    enabled: false, // only fetch on demand
  });

  // ── Public API ──────────────────────────────────────────────────────
  const sendMessage = useCallback(
    (message, imageUrl, attachments, replyTo) => {
      if (!message?.trim() && !imageUrl && !attachments?.length) return;
      sendMutation.mutate({
        message: (message || "").trim(),
        imageUrl,
        attachments,
        replyTo,
      });
    },
    [sendMutation],
  );

  const uploadScreenshot = useCallback(
    async (file) => {
      const result = await uploadMutation.mutateAsync(file);
      return result?.url;
    },
    [uploadMutation],
  );

  const closeConversation = useCallback(() => {
    // Sem mensagem na tela não há conversa a fechar: chamar o servidor só para
    // ouvir "não há nada aberto" gastava uma ida e piscava um erro na tela.
    if (!messages.length) return;
    closeMutation.mutate();
  }, [closeMutation, messages.length]);

  const stopGeneration = useCallback(() => {
    stopMutation.mutate();
  }, [stopMutation]);

  const loadHistory = useCallback(() => {
    historyQuery.refetch();
  }, [historyQuery]);

  /**
   * "Estou digitando", do widget para o Inbox.
   *
   * Vai por socket e não por rota: é um sinal contínuo enquanto alguém escreve,
   * e uma requisição HTTP por tecla seria peso puro. O servidor confere a posse
   * do ticket antes de repassar.
   *
   * Estrangulado aqui TAMBÉM, e não só no servidor: emitir a cada tecla
   * encheria o socket de eventos que o outro lado descartaria de qualquer
   * forma. E o "parou de digitar" é por silêncio — ninguém avisa que parou.
   */
  const typingRef = useRef({ lastSent: 0, stopTimer: null });
  const notifyTyping = useCallback(() => {
    if (!socket || !handover?.active || !handover?.ticketId) return;

    const state = typingRef.current;
    const now = Date.now();

    if (now - state.lastSent > 1000) {
      state.lastSent = now;
      socket.emit("support:typing", { ticketId: handover.ticketId, isTyping: true });
    }

    clearTimeout(state.stopTimer);
    state.stopTimer = setTimeout(() => {
      state.lastSent = 0;
      socket.emit("support:typing", { ticketId: handover.ticketId, isTyping: false });
    }, 2500);
  }, [socket, handover]);

  // O timer não pode sobreviver à tela: dispararia um "parou de digitar" numa
  // conversa que já não está aberta.
  useEffect(() => () => clearTimeout(typingRef.current.stopTimer), []);

  /**
   * A pessoa leu o que o time mandou.
   *
   * Roda quando a conversa está aberta com atendimento humano em curso: é o
   * que vira "lido" na tela do agente, que de outra forma ficaria olhando dois
   * tiques cinzentos para sempre.
   */
  const lastReadRef = useRef(null);
  useEffect(() => {
    if (!handover?.active || !handover?.ticketId) return;

    const lastAgentMessage = [...messages].reverse().find((m) => m.role === "agent");
    if (!lastAgentMessage) return;

    const marker = `${handover.ticketId}:${lastAgentMessage.id}`;
    if (lastReadRef.current === marker) return;
    lastReadRef.current = marker;

    apiPost(`${SUPPORT_API}/handover/read`, { ticketId: handover.ticketId }).catch(() => {
      // Falhar aqui só atrasa um tique na tela do agente — não vale um toast.
      lastReadRef.current = null;
    });
  }, [handover, messages]);

  /**
   * A pessoa reagindo a uma mensagem do time.
   *
   * O emoji pinta na hora e volta atrás se o servidor recusar — reagir é um
   * gesto, não um formulário. Emoji vazio remove, o mesmo contrato do Inbox.
   */
  const reactMutation = useMutation({
    mutationFn: async ({ messageId, emoji }) => {
      const res = await apiPost(`${SUPPORT_API}/handover/reaction`, {
        ticketId: handover?.ticketId,
        messageId,
        emoji,
      });
      if (!res.ok) throw new Error("Não consegui registrar a reação.");
      return res.json();
    },
    onMutate: ({ messageId, emoji }) => {
      const previous = messages.find((m) => m.inboxMessageId === messageId)?.reactions || [];
      setMessages((prev) =>
        prev.map((m) =>
          m.inboxMessageId === messageId
            ? {
                ...m,
                reactions: emoji ? [{ emoji, fromContact: true, fromAgent: false }] : [],
              }
            : m,
        ),
      );
      return { messageId, previous };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.inboxMessageId === context.messageId ? { ...m, reactions: context.previous } : m,
        ),
      );
    },
  });

  const reactToMessage = useCallback(
    (messageId, emoji) => {
      if (!messageId || !handover?.ticketId) return;
      reactMutation.mutate({ messageId, emoji: emoji || "" });
    },
    [reactMutation, handover],
  );

  const requestHuman = useCallback(
    (handoff) => {
      if (handoverMutation.isPending) return;
      handoverMutation.mutate(handoff?.detail || handoff?.reason || null);
    },
    [handoverMutation],
  );

  const rateAnswer = useCallback(
    (messageId, { helpful, comment } = {}) => {
      // Sem id gravado não há o que avaliar: a chave da avaliação é a mensagem.
      if (!messageId || typeof helpful !== "boolean") return;
      rateMutation.mutate({ messageId, helpful, comment });
    },
    [rateMutation],
  );

  return {
    // State
    messages,
    isTyping,
    activity,
    isLoading: sendMutation.isPending,
    isUploading: uploadMutation.isPending,
    conversationId,
    handover,
    isRequestingHuman: handoverMutation.isPending,
    isLoadingConversation: conversationQuery.isLoading,
    history: historyQuery.data,
    isLoadingHistory: historyQuery.isFetching,

    // Actions
    sendMessage,
    stopGeneration,
    uploadScreenshot,
    closeConversation,
    loadHistory,
    rateAnswer,
    requestHuman,
    notifyTyping,
    reactToMessage,
  };
}

export default useSupportChat;
